import { TextField, Typography, Box } from '@mui/material';
import React, { FC, useMemo } from 'react';

function previewUrn(sgln: string): string {
    const trimmed = sgln.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('urn:epc:id:sgln:')) return trimmed;
    return `urn:epc:id:sgln:${trimmed}`;
}

function isValidSglnInput(value: string): boolean {
    const v = value.trim();
    if (!v) return true;
    if (v.startsWith('urn:epc:id:sgln:')) {
        const body = v.slice('urn:epc:id:sgln:'.length);
        return /^\d+\.\.\d+$/.test(body) || /^\d+\.\d+\.\d+$/.test(body);
    }
    return /^\d+\.\.\d+$/.test(v) || /^\d+\.\d+\.\d+$/.test(v);
}

export interface SglnFieldProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    helperText?: string;
    error?: boolean;
    readOnly?: boolean;
}

const SglnField: FC<SglnFieldProps> = ({
    id,
    label,
    value,
    onChange,
    onBlur,
    placeholder = '120018072441..0',
    helperText,
    error,
    readOnly = false,
}) => {
    const preview = useMemo(() => previewUrn(value), [value]);
    const invalid = value.trim() !== '' && !isValidSglnInput(value);

    return (
        <Box>
            <TextField
                size="small"
                fullWidth
                id={id}
                name={id}
                label={label}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                InputProps={{ readOnly }}
                error={!readOnly && (error || invalid)}
                helperText={
                    invalid
                        ? 'Use shorthand with .. (e.g. 120018072441..0) or three-part (e.g. 1200180433226.724416.0)'
                        : helperText
                }
            />
            {preview ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    EPCIS URN preview: {preview}
                </Typography>
            ) : null}
        </Box>
    );
};

export default SglnField;
